// src/pages/builder/IntakeFormDynamic.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box, Container, Typography, Grid, TextField, MenuItem, Button, Card, CardContent,
  Stack, Snackbar, Alert, InputAdornment,
} from '@mui/material';

import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';
import { apipostPublic } from '../../service/api';
import ConfettiModal from '../../components/confetti/confettimodal';

const DEFAULT_CFG = {
  slug: '',
  title: 'Lead Intake',
  subtitle: '',
  intro: '',
  brandColor: '#3e4b59',
  logoSrc: '',
  endpoint: '/lead/webhook',
  loanOfficers: [],
  agentNames: [],
  communities: [],
  forwardCommitments: [],
  urgency: [],
  dateFields: [],
  fieldLabelOverrides: {},
  smsOptIn: { enabled: false, consentText: '' },
};

export default function IntakeFormDynamic({ config, slug }) {
  const [showConfetti, setShowConfetti] = useState(false);

  // 🔹 Merge backend config with defaults
  const cfg = useMemo(
    () => ({ ...DEFAULT_CFG, ...(config || {}) }),
    [config]
  );

  useEffect(() => {
    document.title = `${cfg.title} | Jet HomeLoans`;
  }, [cfg.title]);

  const [form, setForm] = useState({
    loanOfficer: '',
    agentName: '',
    agentPhone: '',
    agentEmail: '',
    buyerFirst: '',
    buyerLast: '',
    buyerEmail: '',
    buyerPhone: '',
    community: '',
    zip: '',
    lot: '',
    price: '',
    fwdCommit: '',
    estClose: '',
    incentiveMax: '',
    solar: '',
    hoa: '',
    taxRate: '',
    realtorName: '',
    realtorPhone: '',
    realtorEmail: '',
    rapport: '',
    urgency: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const fieldRefs = {
     loanOfficer: useRef(null), 
    agentName: useRef(null),
    agentPhone: useRef(null),
    agentEmail: useRef(null),
    buyerFirst: useRef(null),
    buyerLast: useRef(null),
    buyerEmail: useRef(null),
    buyerPhone: useRef(null),
    community: useRef(null),
    zip: useRef(null),
    fwdCommit: useRef(null),
    estClose: useRef(null),
    rapport: useRef(null),
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    const e = {};

      if (Array.isArray(cfg.loanOfficers) && cfg.loanOfficers.length > 0) {
      if (!form.loanOfficer) e.loanOfficer = 'Required';
    }

    if (!form.agentName) e.agentName = 'Required';
    if (!form.agentPhone) e.agentPhone = 'Required';
    if (!form.agentEmail) e.agentEmail = 'Required';
    if (!form.buyerFirst) e.buyerFirst = 'Required';
    if (!form.buyerLast) e.buyerLast = 'Required';
    if (!form.buyerEmail) e.buyerEmail = 'Required';
    if (!form.buyerPhone) e.buyerPhone = 'Required';
    if (!form.community) e.community = 'Required';
    if (!form.zip) e.zip = 'Required';
    if (form.fwdCommit === '') e.fwdCommit = 'Required';
    if (!form.estClose) e.estClose = 'Required';
    if (!form.rapport) e.rapport = 'Required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const v = validate();
    setErrors(v);

    if (Object.keys(v).length > 0) {
      const firstKey = Object.keys(v)[0];
      const ref = fieldRefs[firstKey];
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = ref.current.querySelector('input, textarea, select');
        if (input) input.focus();
      }
      setSnack({
        open: true,
        severity: 'error',
        message: 'Please complete all required fields.',
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        slug,
        leadSource: cfg.candidSlug || cfg.title,
        referralSource: cfg.candidSlug || cfg.title,
          // ✅ Lead model–correct fields
  firstName: form.buyerFirst,
  lastName: form.buyerLast,
  phoneNumber: form.buyerPhone,
  emailAddress: form.buyerEmail,

  // ✅ keep everything else
  loanOfficer: form.loanOfficer,
  agentName: form.agentName,
  agentPhone: form.agentPhone,
  agentEmail: form.agentEmail,
  community: form.community,
  zip: form.zip,
  lot: form.lot,
  price: form.price,
  fwdCommit: form.fwdCommit,
  estClose: form.estClose,
  incentiveMax: form.incentiveMax,
  solar: form.solar,
  hoa: form.hoa,
  taxRate: form.taxRate,
  realtorName: form.realtorName,
  realtorPhone: form.realtorPhone,
  realtorEmail: form.realtorEmail,
  additionalDetails: form.rapport,
  urgency: form.urgency,
      };

      const resp = await apipostPublic(cfg.endpoint, payload);
      const success = !!resp?.data?.lead?._id;
      if (!success) throw new Error(resp?.data?.message || 'Request failed');

      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);

      setSnack({
        open: true,
        severity: 'success',
        message: 'Submitted! A Jet HomeLoans teammate will reach out shortly.',
      });

      setForm({
        loanOfficer: '',
        agentName: '',
        agentPhone: '',
        agentEmail: '',
        buyerFirst: '',
        buyerLast: '',
        buyerEmail: '',
        buyerPhone: '',
        community: '',
        zip: '',
        lot: '',
        price: '',
        fwdCommit: '',
        estClose: '',
        incentiveMax: '',
        solar: '',
        hoa: '',
        taxRate: '',
        realtorName: '',
        realtorPhone: '',
        realtorEmail: '',
        rapport: '',
        urgency: '',
      });
    } catch (err) {
      setSnack({
        open: true,
        severity: 'error',
        message: err?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ConfettiModal open={showConfetti} onClose={() => setShowConfetti(false)} />
      <LandingPageNav />

      <Container maxWidth="lg" sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 10 } }}>
        <Card elevation={4} sx={{ borderRadius: 2 }}>
          {/* Brand bar */}
          <Box
            sx={{
              bgcolor: cfg.brandColor,
              py: 4,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {cfg.logoSrc && (
              <Box
                component="img"
                src={cfg.logoSrc}
                alt={`${cfg.title} logo`}
                sx={{ height: 60, objectFit: 'contain' }}
              />
            )}
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 900, color: '#2e3a49' }}>
                {cfg.title}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ letterSpacing: 4, color: 'text.secondary', fontWeight: 600 }}
              >
                {cfg.subtitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 2, color: 'text.secondary', fontSize: '1.125rem' }}
              >
                {cfg.intro}
              </Typography>
            </Stack>

            {/* ===== FORM ===== */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>

                {/* Loan Officer */}
             {Array.isArray(cfg.loanOfficers) && cfg.loanOfficers.length > 0 && (
  <Grid item xs={12} ref={fieldRefs.loanOfficer}>
    <TextField
      required // ✅ add
      select
      fullWidth
      label="Loan Officer"
      name="loanOfficer"
      value={form.loanOfficer}
      onChange={onChange}
      error={!!errors.loanOfficer}
      helperText={errors.loanOfficer}
    >
      <MenuItem value="">
        <em>Select…</em>
      </MenuItem>

      {cfg.loanOfficers
        .filter((lo) => lo !== 'Select…')
        .map((lo) => (
          <MenuItem key={lo} value={lo}>
            {lo}
          </MenuItem>
        ))}
    </TextField>
  </Grid>
)}


                {/* Agent Name */}
                <Grid item xs={12} md={6} ref={fieldRefs.agentName}>
                  {Array.isArray(cfg.agentNames) && cfg.agentNames.length > 0 ? (
                    <TextField
                      required
                      select
                      fullWidth
                      label="Agent Name"
                      name="agentName"
                      value={form.agentName}
                      onChange={onChange}
                      error={!!errors.agentName}
                      helperText={errors.agentName}
                    >
                      <MenuItem value="">
                        <em>Select Agent…</em>
                      </MenuItem>
                      {cfg.agentNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      required
                      fullWidth
                      label="Agent Name"
                      name="agentName"
                      value={form.agentName}
                      onChange={onChange}
                      error={!!errors.agentName}
                      helperText={errors.agentName}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={6} ref={fieldRefs.agentPhone}>
                  <TextField
                    required
                    fullWidth
                    label="Agent Phone"
                    name="agentPhone"
                    value={form.agentPhone}
                    onChange={onChange}
                    error={!!errors.agentPhone}
                    helperText={errors.agentPhone}
                  />
                </Grid>

                <Grid item xs={12} ref={fieldRefs.agentEmail}>
                  <TextField
                    required
                    fullWidth
                    label="Agent Email"
                    name="agentEmail"
                    type="email"
                    value={form.agentEmail}
                    onChange={onChange}
                    error={!!errors.agentEmail}
                    helperText={errors.agentEmail}
                  />
                </Grid>

                {/* Buyer */}
                <Grid item xs={12} md={6} ref={fieldRefs.buyerFirst}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer First Name"
                    name="buyerFirst"
                    value={form.buyerFirst}
                    onChange={onChange}
                    error={!!errors.buyerFirst}
                    helperText={errors.buyerFirst}
                  />
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.buyerLast}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer Last Name"
                    name="buyerLast"
                    value={form.buyerLast}
                    onChange={onChange}
                    error={!!errors.buyerLast}
                    helperText={errors.buyerLast}
                  />
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.buyerEmail}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer Email"
                    name="buyerEmail"
                    type="email"
                    value={form.buyerEmail}
                    onChange={onChange}
                    error={!!errors.buyerEmail}
                    helperText={errors.buyerEmail}
                  />
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.buyerPhone}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer Phone"
                    name="buyerPhone"
                    value={form.buyerPhone}
                    onChange={onChange}
                    error={!!errors.buyerPhone}
                    helperText={errors.buyerPhone}
                  />
                </Grid>

                {/* Community & Property */}
                <Grid item xs={12} md={6} ref={fieldRefs.community}>
                  <TextField
                    select
                    required
                    fullWidth
                    label="Select Community"
                    name="community"
                    value={form.community}
                    onChange={onChange}
                    error={!!errors.community}
                    helperText={errors.community}
                  >
                    {cfg.communities.map((c) => (
                      <MenuItem key={c} value={c === 'Select Community…' ? '' : c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.zip}>
                  <TextField
                    required
                    fullWidth
                    label="Zip Code "
                    name="zip"
                    value={form.zip}
                    onChange={onChange}
                    error={!!errors.zip}
                    helperText={errors.zip}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Lot Number" name="lot" value={form.lot} onChange={onChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sales Price ($)"
                    name="price"
                    value={form.price}
                    onChange={onChange}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>

                <Grid item xs={12} md={6} ref={fieldRefs.fwdCommit}>
                  <TextField
                  required
                    select
                    fullWidth
                    label="Forward Commitment"
                    name="fwdCommit"
                    value={form.fwdCommit}
                    onChange={onChange}
                    error={!!errors.fwdCommit}
                    helperText={errors.fwdCommit}
                  >
                    {cfg.forwardCommitments.map((opt) => (
                      <MenuItem key={opt} value={opt === 'None' ? 'None' : opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.estClose}>
                  <TextField
                    required
                    fullWidth
                    type="date"
                    label="Est Closing/Completion Date"
                    name="estClose"
                    value={form.estClose}
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.estClose}
                    helperText={errors.estClose}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Incentive Available Max" name="incentiveMax" value={form.incentiveMax} onChange={onChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Solar" name="solar" value={form.solar} onChange={onChange} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="HOA ($)"
                    name="hoa"
                    value={form.hoa}
                    onChange={onChange}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Tax Rate" name="taxRate" value={form.taxRate} onChange={onChange} />
                </Grid>

                {/* Realtor */}
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Realtor Name" name="realtorName" value={form.realtorName} onChange={onChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Realtor Phone" name="realtorPhone" value={form.realtorPhone} onChange={onChange} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Realtor Email" name="realtorEmail" type="email" value={form.realtorEmail} onChange={onChange} />
                </Grid>

                {/* Rapport */}
                <Grid item xs={12} ref={fieldRefs.rapport}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={5}
                    label="Anything you can tell us to build rapport? "
                    name="rapport"
                    value={form.rapport}
                    onChange={onChange}
                    error={!!errors.rapport}
                    helperText={errors.rapport}
                  />
                </Grid>

                {/* Urgency */}
                <Grid item xs={12} ref={fieldRefs.urgency}>
                  <TextField
                    select
                    fullWidth
                    label="Urgency "
                    name="urgency"
                    value={form.urgency}
                    onChange={onChange}
                    error={!!errors.urgency}
                    helperText={errors.urgency}
                  >
                    {cfg.urgency.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      bgcolor: '#3e4b59',
                      '&:hover': { bgcolor: '#2e3946' },
                      py: 1.4,
                      letterSpacing: 1,
                      fontWeight: 700,
                    }}
                  >
                    {loading ? 'SUBMITTING…' : 'SUBMIT'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Footer />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
      <ConfettiModal open={showConfetti} onClose={() => setShowConfetti(false)} />
    </>
  );
}
